import { SvelteMap } from "svelte/reactivity";
import { client } from "$lib/state/client.js";
import { workspaceState } from "$lib/core/state.svelte.js";
import { reportUiFailure } from "$lib/report-failure.js";

// The relay's closed refusal codes (server a2a-refusal.ts A2A_REFUSAL_TEXT) -> the words the ask card shows.
const REFUSAL_TEXT: Record<string, string> = {
  no_pro: "requires Pro",
  not_member: "that agent is not a member of this channel",
  unresolvable: "that agent belongs to another workspace",
  no_initiator: "no person to run it for",
  chain_budget: "this chain of agent calls hit its limit",
  claim_denied: "that agent was already called in this chain",
  channel_missing: "the channel no longer exists",
};

// The text of the relay's system message in the ask's thread.
const SYSTEM_LINE_RE = /^Not delivered — (.+)\.$/;

// Keyed by the ASK message id, never the thread: a thread holds many asks and only one may be refused.
const refused = new SvelteMap<string, string>();
// Ask id -> when a read last found no refusal for it: asked again only after RECHECK_MS (the refusal may post a moment later).
const checked = new SvelteMap<string, number>();

/** The reason this ask was refused ("requires Pro"), or undefined while nothing says it was. */
export const refusalOfAsk = (askId: string): string | undefined => refused.get(askId);

/** Live: the broadcast's a2aRefusal names the ask message. */
export function noteA2aRefusal(msg: {
  text: string;
  a2aRefusal?: { askMessageId: string; reason: string };
}): void {
  const r = msg.a2aRefusal;
  if (!r) return;
  refused.set(
    r.askMessageId,
    REFUSAL_TEXT[r.reason] ?? SYSTEM_LINE_RE.exec(msg.text)?.[1] ?? r.reason,
  );
}

interface ThreadMsg {
  id: string;
  fromType: string;
  text: string;
}

// The relay's refusal message has the deterministic id `a2a-refusal-<askMessageId>`: match THAT first.
// Fallback (no message has that id, e.g. an older server): the system line right after the ask, or the thread's
// first reply when the ask is the root (fetchThread returns replies only). Position fails if another reply lands between.
function refusalOf(
  askId: string,
  threadId: string,
  messages: readonly ThreadMsg[],
): string | undefined {
  const byId = messages.find((m) => m.id === `a2a-refusal-${askId}`);
  if (byId) return SYSTEM_LINE_RE.exec(byId.text)?.[1];
  if (messages.some((m) => m.id.startsWith("a2a-refusal-"))) return undefined; // ids exist: this ask has none
  const at = askId === threadId ? -1 : messages.findIndex((m) => m.id === askId);
  if (askId !== threadId && at < 0) return undefined;
  const next = messages[at + 1];
  return next?.fromType === "system" ? SYSTEM_LINE_RE.exec(next.text)?.[1] : undefined;
}

// ponytail: at most MAX_PARALLEL thread reads at once, only asks younger than MAX_AGE_MS. Upgrade path: a
// per-ask refusal endpoint (one small read instead of a thread fetch).
const MAX_PARALLEL = 3;
export const REFUSAL_CHECK_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;
let running = 0;
const waiting: Array<() => void> = [];
// A refusal lands seconds after its ask: a read (and a "no refusal" verdict) is reused for RECHECK_MS only, and an
// ask younger than that is left to the live a2aRefusal broadcast.
export const REFUSAL_RECHECK_MS = 10_000;
const threadReads = new Map<string, { at: number; read: Promise<ThreadMsg[]> }>();

async function slot<T>(work: () => Promise<T>): Promise<T> {
  if (running >= MAX_PARALLEL) await new Promise<void>((resolve) => waiting.push(resolve));
  running += 1;
  try {
    return await work();
  } finally {
    running -= 1;
    waiting.shift()?.();
  }
}

// An ask younger than this is rechecked every RECHECK_MS (and on a timer, so an idle stream still gets it); an older ask
// is read once and that verdict is kept.
export const REFUSAL_YOUNG_MS = 2 * 60 * 1000;
const timers = new Map<string, ReturnType<typeof setTimeout>>();

/** Stop the pending recheck timer of this ask (its card went away). */
export function cancelAskRefusalCheck(askId: string): void {
  clearTimeout(timers.get(askId));
  timers.delete(askId);
}

// One pending timer per ask while it is young: the refusal can land with the stream idle.
function scheduleRecheck(ask: { askId: string; threadId: string; at?: number }, age: number): void {
  if (ask.at === undefined || age >= REFUSAL_YOUNG_MS || timers.has(ask.askId)) return;
  const fire = () => {
    timers.delete(ask.askId);
    void checkAskForRefusal(ask);
  };
  timers.set(ask.askId, setTimeout(fire, REFUSAL_RECHECK_MS + 100));
}

/** After a reload: match this ask's refusal line in its thread (one read per thread, 3 at a time, recent asks only). */
export async function checkAskForRefusal(ask: {
  askId: string;
  threadId: string;
  at?: number;
}): Promise<void> {
  const wsId = workspaceState.current?.id;
  if (!wsId || refused.has(ask.askId)) return;
  const now = Date.now();
  const age = ask.at === undefined ? 0 : now - ask.at;
  if (age > REFUSAL_CHECK_MAX_AGE_MS) return;
  const last = checked.get(ask.askId);
  if (
    ask.at !== undefined &&
    age >= REFUSAL_YOUNG_MS &&
    last !== undefined &&
    last >= ask.at + REFUSAL_YOUNG_MS
  )
    return;
  scheduleRecheck(ask, age);
  if (now - (last ?? -Infinity) < REFUSAL_RECHECK_MS) return;
  if (age < REFUSAL_RECHECK_MS && ask.at !== undefined) return;
  checked.set(ask.askId, now);
  try {
    let entry = threadReads.get(ask.threadId);
    if (!entry || now - entry.at >= REFUSAL_RECHECK_MS) {
      entry = {
        at: now,
        read: slot(
          async () => (await client.fetchThread(wsId, ask.threadId)).messages as ThreadMsg[],
        ),
      };
      threadReads.set(ask.threadId, entry);
    }
    const why = refusalOf(ask.askId, ask.threadId, await entry.read);
    if (why) refused.set(ask.askId, why);
  } catch (err) {
    threadReads.delete(ask.threadId);
    checked.delete(ask.askId);
    reportUiFailure("a2a.refusal_thread", err, { threadId: ask.threadId });
  }
}

/** Test-only. */
export function resetA2aRefusals(): void {
  refused.clear();
  checked.clear();
  for (const t of timers.values()) clearTimeout(t);
  timers.clear();
  threadReads.clear();
  waiting.length = 0;
  running = 0;
}
